import type { FC } from "react";
import { useEffect, useState } from "react";
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useGeographySearch } from "src/api/sure-api/geography/useGeographySearch";
import {
    formatGeographyLabel,
    mergeGeographyOptions,
    type Geography,
} from "src/api/sure-api/geography/geographySchema";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

interface GeographyAutocompleteProps {
    value: Geography | null;
    onChange: (option: Geography | null) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: object;
}

export const GeographyAutocomplete: FC<GeographyAutocompleteProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    sx = {},
}) => {
    const { t } = useTranslation();
    const { delayedSearch, setSearch, resetSearch } = useDelayedSearch({ defaultDelay: 300 });
    const [inputValue, setInputValue] = useState("");

    const trimmedSearch = delayedSearch.trim();
    const hasSearch = trimmedSearch.length > 0;

    const { geographies = [], isLoading } = useGeographySearch({
        searchText: delayedSearch,
        enabled: hasSearch,
    });

    const options = mergeGeographyOptions([value], geographies);

    useEffect(() => {
        if (value) {
            setInputValue(formatGeographyLabel(value));
            resetSearch();
        }
    }, [value]);

    return (
        <Autocomplete
            options={options}
            value={value}
            inputValue={inputValue}
            onChange={(_, newValue) => {
                onChange(newValue);
                setInputValue(newValue ? formatGeographyLabel(newValue) : "");
                resetSearch();
            }}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") {
                    setInputValue(newInputValue);
                    return;
                }

                setInputValue(newInputValue);
                if (reason === "input") {
                    setSearch(newInputValue);
                }
            }}
            getOptionLabel={(option) => formatGeographyLabel(option)}
            isOptionEqualToValue={(option, selected) => option.code === selected.code}
            loading={isLoading}
            disabled={disabled}
            filterOptions={(items) => items}
            noOptionsText={
                isLoading ? t("common.loading", "Loading...")
                : hasSearch ?
                    t("common.noResults", "No results")
                :   t("geography.select.typeToSearch", "Type a geography code or name")
            }
            loadingText={t("common.loading", "Loading...")}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.code} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip label={option.code} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />
                    <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {option.name}
                    </Typography>
                    <Chip
                        label={option.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem", textTransform: "capitalize" }}
                    />
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={
                        placeholder ??
                        t("geography.select.placeholder", "Search geography by code or name (e.g. FR, France)")
                    }
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && hasSearch ?
                                    <CircularProgress color="inherit" size={20} />
                                :   null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            backgroundColor: "#FFFFFF",
                            minHeight: "40px",
                            "& fieldset": {
                                borderColor: "#E7E6E4",
                            },
                            "&:hover fieldset": {
                                borderColor: "#D0D0D0",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                            },
                        },
                        "& .MuiOutlinedInput-input": {
                            fontSize: "0.875rem",
                            padding: "0px 16px !important",
                            color: "#393A36",
                            "&::placeholder": {
                                color: "#A3A2A0",
                                opacity: 1,
                            },
                        },
                        ...sx,
                    }}
                />
            )}
        />
    );
};
